// eslint-disable-next-line import/named
import { IServerSideGetRowsRequest, SetFilterValuesFuncParams } from 'ag-grid-community'
import {
  ObGridFilterByDateQuery,
  ObGridFilterByNumberQuery,
  obGridSortQueryGenerator,
  setFilterWidthDynamically,
} from 'components/ObGrid/ObGridUtils'
import { apiPaths, RelativeRootEnum } from 'configs/apiConfigs'

import { OperatorTypeEnum } from 'types/tableTypes'
import { encodeUri } from 'utils'
import { httpClaims, httpTenantsService } from 'utils/http'

import { USER_LIMIT } from 'features/claims'
import { CSCreateQuery, CSRestQuery, Sort } from 'features/claims/claimTypes'
import { getAvailableUsername } from 'features/claims/components/ClaimsTable/Internal/BulkAssignAndUnAssign'
import { getUnmatchedEobs } from '../autoCheckApi'
import { BatchProcessingStatus, BatchTabType } from '../types'
import { UserPreferencesKeysEnum } from 'features/settings/settingsTypes'

export const createBatchServerSideDataSource = (root: BatchTabType) => {
  return {
    getRows: async (params: any) => {
      params.api.showLoadingOverlay()
      params.api.showNoRowsOverlay()
      if (params.request.filterModel && params.request.filterModel['uploaderUserName']) {
        const oaiUserQuery = new CSRestQuery(USER_LIMIT, 0)
        const preferredTenant = sessionStorage.getItem(UserPreferencesKeysEnum.outboundAiPreferredTenant)
        oaiUserQuery.addSort(new Sort('givenName'))
        oaiUserQuery.addSort(new Sort('familyName'))

        const manageQuery = new CSCreateQuery(oaiUserQuery)
        const prepareUrl = `tenants/${preferredTenant}/${RelativeRootEnum.users}`
        const url = apiPaths.userSearch(prepareUrl, manageQuery).replace(',', '')
        const updatedUrl = url.replace(',', '')
        const response = await httpTenantsService.get(updatedUrl)
        const valueList = response.data.data
        params.request.filterModel['uploaderUserName']['values'].forEach((item: any, index: number) => {
          const dataItem = valueList.find((data: any) => item === getAvailableUsername(data))
          if (dataItem) {
            params.request.filterModel['uploaderUserName']['values'][index] = dataItem.userGuid
          }
        })
      }
      const builtQuery = buildQuery(params.request, root)
      const dataQuery = builtQuery ? `?${builtQuery}` : ''
      fetchClaimsWithBuiltQuery(params, dataQuery, root)
      return params
    },
  }
}

export const buildQuery = (paramsRequest: IServerSideGetRowsRequest, root: BatchTabType) => {
  const query: string[] = []
  const currentPreferredTenant = sessionStorage.getItem(UserPreferencesKeysEnum.outboundAiPreferredTenant)

  const initialFilterValue = getInitialFilterValue(root)

  if (root === BatchTabType.Archived) {
    query.push(obGridFilterQueryGenerator('archived', '=', 'true'))
  } else if (root === BatchTabType.EOBReview) {
    query.push(obGridFilterQueryGenerator('tenantOwner', '=', currentPreferredTenant!)!)
  } else {
    query.push(obGridFilterQueryGenerator('processingStatus', 'in', initialFilterValue)!)
    query.push(obGridFilterQueryGenerator('archived', '=', 'false'))
  }

  query.push(`Limit=20`)
  query.push(`Offset=${paramsRequest.startRow}`)

  const sortModel = paramsRequest.sortModel
  // const initialSortQuery =
  //   sortModel.length > 0
  //     ? obGridSortQueryGenerator(sortModel[0].colId, sortModel[0].sort === 'desc')
  //     : root === BatchTabType.EOBReview || root === BatchTabType.UnSupportedEob
  //     ? obGridSortQueryGenerator('dateReceived', true)
  //     : obGridSortQueryGenerator('created', true)
  // query.push(initialSortQuery)

  const initialSortQuery =
    sortModel.length > 0
      ? obGridSortQueryGenerator(sortModel[0].colId, sortModel[0].sort === 'desc')
      : (() => {
        switch (root) {
          case BatchTabType.EOBReview:
            return obGridSortQueryGenerator('unmatchedDate', true)
          case BatchTabType.UnSupportedFax:
            return obGridSortQueryGenerator('dateReceived', true)
          default:
            return obGridSortQueryGenerator('created', true)
        }
      })()

  query.push(initialSortQuery)

  const mappedQuery = getMappedApiQueryBasedOnClaimsFilter(paramsRequest.filterModel, query)
  return mappedQuery
}

function getInitialFilterValue(root: BatchTabType): BatchProcessingStatus[] {
  switch (root) {
    case BatchTabType.Processed:
      return [BatchProcessingStatus.Complete, BatchProcessingStatus.Cancelled, BatchProcessingStatus.FailedToComplete]
    case BatchTabType.EOB:
      return [BatchProcessingStatus.Awaiting_EOB]
    case BatchTabType.Processing:
      return [
        BatchProcessingStatus.AutoCheck_In_Queue,
        BatchProcessingStatus.Ready_for_Processing,
        BatchProcessingStatus.Cancelling,
        BatchProcessingStatus.AutoCheck_In_Progress,
      ]
    case BatchTabType.Claims:
      return [BatchProcessingStatus.Pending_Approval, BatchProcessingStatus.Uploading_Claims]
    default:
      return []
  }
}

export const userNameFilterParams = async () => {
  const oaiUserQuery = new CSRestQuery(USER_LIMIT, 0)
  const preferredTenant = sessionStorage.getItem(UserPreferencesKeysEnum.outboundAiPreferredTenant)
  //oaiUserQuery.addFilter(new Filter('_permissionId', null, IUserRoleGuidEnum.claimAccessPermission!))

  oaiUserQuery.addSort(new Sort('givenName'))
  oaiUserQuery.addSort(new Sort('familyName'))

  const manageQuery = new CSCreateQuery(oaiUserQuery)
  const prepareUrl = `tenants/${preferredTenant}/${RelativeRootEnum.users}`
  const url = apiPaths.userSearch(prepareUrl, manageQuery).replace(',', '')
  const updatedUrl = url.replace(',', '')
  const value = await httpTenantsService.get(updatedUrl)
  const listValues = value.data.data.map((item: any) => {
    return getAvailableUsername(item)
  })
  return listValues
}

export const filterParams = {
  defaultToNothingSelected: true,
  values: async (params: SetFilterValuesFuncParams) => {
    const listValues = await userNameFilterParams()
    listValues.unshift(null)
    setFilterWidthDynamically(listValues)
    params.success(listValues)
  },
  buttons: ['apply', 'cancel'],
  closeOnApply: true,
}

export const fetchClaimsWithBuiltQuery = (params: any, dataQuery: string, root: BatchTabType) => {
  // const unmatchedUnSupportedEob =
  //   root === BatchTabType.EOBReview ? getUnmatchedEobs(dataQuery) : getUnSupportedFaxes(dataQuery)

  root === BatchTabType.EOBReview
    ? getUnmatchedEobs(dataQuery).then((response) => {
      const data = response
      const noDataAvailable: boolean = (data.count === 0 && data.data.length <= 0) ?? true
      if (noDataAvailable) {
        params.api.showNoRowsOverlay()
      } else {
        params.api.hideOverlay()
      }

      params.success({ rowData: data.data, rowCount: data.count })
    })
    : httpClaims
      .get(apiPaths.batches(dataQuery))
      .then((response) => {
        const data = response

        // filter data.data.data to find is processingStatus === 'Pending_Approval'
        // if yes then hide column 'Actions'

        const isPendingForReview = data.data.data.some(
          (item: any) => item.processingStatus === BatchProcessingStatus.Pending_Approval,
        )
        if (root === BatchTabType.Claims) {
          params.api.setColumnsVisible(['upgradeBatch'], isPendingForReview)
        }

        /**
         * If no data available then we need to show 'No data available Message'
         */
        const noDataAvailable: boolean = (data.data.count === 0 && data.data.data.length <= 0) ?? true
        if (noDataAvailable) {
          params.api.showNoRowsOverlay()
        } else {
          params.api.hideOverlay()
        }

        params.success({ rowData: data.data.data, rowCount: data.data.count })
      })
      .catch((error) => {
        params.api.hideOverlay()
        console.log(error)
      })
}

export function obGridFilterQueryGenerator(
  path: string,
  operator: string,
  value: Array<string> | string | Array<number>,
) {
  const filterValue = Array.isArray(value)
  if (filterValue) {
    return `Filter=${encodeUri({
      path: path === 'uploaderUserName' ? 'userId' : path,
      operator: operator,
      valueList: value,
    })}`
  } else {
    return `Filter=${encodeUri({
      path: path,
      operator: operator,
      value: value!,
    })}`
  }
}

const getMappedApiQueryBasedOnClaimsFilter = (filterModel: any, query: Array<string>): string => {
  Object.keys(filterModel).map((key) => {
    switch (filterModel[key].filterType) {
      case OperatorTypeEnum.date:
        query.push(ObGridFilterByDateQuery(filterModel, key, query))
        break
      case OperatorTypeEnum.set:
        query.push(obGridFilterQueryGenerator(key, 'in', filterModel[key].values)!)
        break
      case OperatorTypeEnum.number:
        query.push(ObGridFilterByNumberQuery(filterModel, key, query))
        break
      default:
        query.push(
          `Filter=${encodeUri({
            path: key,
            operator: 'like',
            value: '%' + filterModel[key].filter + '%',
          })}`,
        )
        break
    }
  })
  return query.join('&')
}
